/*
 *  -----------------------------------------------------------------------
 *    <copyright file="action-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogActions } from '@mui/material';
import { Alert } from '@mui/material';
import cn from 'clsx';
import i18n from 'i18next';
import React from 'react';
import { ActionDialogProps } from './action-dialog.interface';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogContent, DialogTitle } from 'views/components/core/dialog';
import './action-dialog.scss';

const ActionDialog = ({
    title,
    warning,
    children,
    onClose,
    onSubmit,
    cancelText = i18n.t('cancel'),
    submitText = i18n.t('delete'),
    submitDisabled,
    asNotification,
    extraBnt,
    extraBntText,
    extraBntOnClick,
    extraBntDisabled,
    className,
    'data-testid': dataTestId,
}: ActionDialogProps) => (
    <CustomDialog
        onClose={onClose}
        aria-labelledby="action-dialog"
        fullWidth
        maxWidth="md"
        scroll="paper"
        open
        className={cn('action-dialog', className)}
        data-testid={dataTestId}
    >
        <DialogTitle>
            {title}
            <FontAwesomeIcon
                icon={['fal', 'times']}
                className="fa-lg k-cursor-pointer"
                onClick={() => onClose()}
                data-testid="close-dialog"
            />
        </DialogTitle>

        <DialogContent className="dialog-content">
            {!!warning && (
                <Alert
                    className="alert alert--warning"
                    severity="warning"
                    icon={<FontAwesomeIcon icon={['fal', 'exclamation-triangle']} />}
                >
                    {warning}
                </Alert>
            )}
            {children}
        </DialogContent>

        {!asNotification && (
            <DialogActions>
                <Button className="btn btn--outlined" text={cancelText} onClick={onClose} data-testid="cancel-button" />
                <Button text={submitText} onClick={onSubmit} disabled={submitDisabled} data-testid="submit-button" />
                {extraBnt && (
                    <Button
                        text={extraBntText}
                        onClick={extraBntOnClick}
                        disabled={extraBntDisabled}
                        data-testid="extra-button"
                    />
                )}
            </DialogActions>
        )}
    </CustomDialog>
);

export default ActionDialog;
